import { registrationClient, userClient } from '../config/api';
import { ApiResponse } from './types/api.types';
import {
  ProviderCompany,
  CreateProviderCompanyRequest,
  UpdateProviderCompanyRequest,
  GetAllProviderCompaniesParams,
  SearchProviderCompaniesParams,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class ProviderCompanyService {
  private readonly basePath = '/api/providers';

  getAllProviderCompanies = async (params?: GetAllProviderCompaniesParams): Promise<ProviderCompany[]> => {
    return this.getAllProviders(params);
  }

  getAllProviders = async (params?: GetAllProviderCompaniesParams): Promise<ProviderCompany[]> => {
    try {
      const response = await registrationClient.get<ApiResponse<ProviderCompany[]>>(this.basePath, {
        params,
      });

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching providers');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching providers');
    }
  }

  getProviderCompanyById = async (id: string): Promise<ProviderCompany> => {
    return this.getProviderById(id);
  }

  getProviderById = async (id: string): Promise<ProviderCompany> => {
    try {
      const response = await registrationClient.get<ApiResponse<ProviderCompany>>(`${this.basePath}/${id}`);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching provider');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching provider');
    }
  }

  createProvider = async (data: CreateProviderCompanyRequest): Promise<ProviderCompany> => {
    try {
      const response = await registrationClient.post<ApiResponse<ProviderCompany>>(this.basePath, data);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error creating provider');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao criar prestadora');
    }
  }

  updateProviderCompany = async (id: string, data: UpdateProviderCompanyRequest): Promise<void> => {
    return this.updateProvider(id, data);
  }

  updateProvider = async (id: string, data: UpdateProviderCompanyRequest): Promise<void> => {
    try {
      const response = await registrationClient.put<ApiResponse<ProviderCompany>>(
        `${this.basePath}/${id}`,
        data
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error updating provider');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao atualizar prestadora');
    }
  }

  deleteProviderCompany = async (id: string): Promise<void> => {
    return this.deleteProvider(id);
  }

  deleteProvider = async (id: string): Promise<void> => {
    try {
      const response = await registrationClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting provider');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao remover prestadora');
    }
  }

  searchProviders = async (params: SearchProviderCompaniesParams): Promise<ProviderCompany[]> => {
    try {
      const response = await registrationClient.get<ApiResponse<ProviderCompany[]>>(
        `${this.basePath}/search`,
        {
          params,
        }
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error searching providers');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error searching providers');
    }
  }

  
  getOperationalProviders = async (masterProviderId: string): Promise<ProviderCompany[]> => {
    try {
      
      const usersResponse = await userClient.get<ApiResponse<any>>(
        `/api/users/providers?providerId=${masterProviderId}&pageSize=1000`
      );

      const users = usersResponse.data.data?.data || [];

      return users.map((user: any) => ({
        id: user.userId, 
        userId: user.userId, 
        providerId: user.providerId, 
        name: user.name, 
        cnpj: '',
        email: '',
        integrationCode: '',
        shortName: user.name,
        minValue: 0,
        maxValue: 0,
        isActive: true,
        allowsDemandReceiving: false,
        phones: [],
        postalCode: '',
        street: '',
        number: '',
        complement: '',
        district: '',
        state: '',
        city: ''
      }));
    } catch (error: any) {
      if (error.response?.data?.errorMessage) {
        throw new Error(error.response.data.errorMessage);
      }

      if (error.response?.data?.errors?.length > 0) {
        throw new Error(error.response.data.errors.join(', '));
      }

      
      return [];
    }
  }
}

export const providerCompanyService = new ProviderCompanyService();
